import { useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/alloyal/cashback`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
export function parseStoreQr(text) {
  const value = JSON.parse(text);
  if (!value || typeof value.confirmation_key !== "string" || !/^(?:\d{14}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/.test(value.confirmation_key)) throw new Error("QR inválido");
  return value.confirmation_key;
}

export default function CouponRedemption({ offer, onIssued }) {
  const readerId = `alloyal-reader-${useId().replace(/:/g, "")}`;
  const [scanning, setScanning] = useState(false);
  const [store, setStore] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const requestId = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!scanning) return;
    let canceled = false, reader;
    const stop = async () => {
      if (reader?.isScanning) { try { await reader.stop(); } catch {} }
      try { reader?.clear(); } catch {}
    };
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (canceled) return;
        reader = new Html5Qrcode(readerId);
        await reader.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 200, height: 200 } }, (text) => {
          if (canceled) return;
          try { const key = parseStoreQr(text); canceled = true; setStore(key); setError(""); setScanning(false); setConfirming(true); }
          catch { setError("Este QR Code não é uma identificação de loja Alloyal. Leia o código indicado pelo estabelecimento."); }
        }, () => {});
        if (canceled) await stop();
      } catch { if (!canceled) { setError("Não foi possível abrir a câmera. Confira a permissão do navegador e tente novamente."); setScanning(false); } }
    })();
    return () => { canceled = true; void stop(); };
  }, [scanning, readerId]);

  const submit = async () => {
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError("");
    requestId.current ||= window.crypto.randomUUID();
    try {
      const { data } = await axios.post(`${API}/redeem`, { request_id: requestId.current, organization_id: offer.organization_id, coupon_id: offer.coupon_id || offer.id, confirmed: true, ...(offer.template === "qrcode" ? { confirmation_key: store } : {}) }, auth());
      if (!mounted.current) return;
      setResult(data); setConfirming(false);
      if (data.state === "issued") onIssued?.();
    } catch (failure) {
      if (!mounted.current) return;
      const status = failure?.response?.status;
      if ([401, 403, 422, 503].includes(status)) {
        setError(failure?.response?.data?.detail || "Não foi possível emitir este cupom.");
      } else {
        setResult({ state: "uncertain", message: "Não foi possível confirmar a emissão. Consulte Meus cupons antes de tentar novamente." }); setConfirming(false);
      }
    } finally { submitting.current = false; if (mounted.current) setBusy(false); }
  };
  const reset = () => { requestId.current = null; setResult(null); setConfirming(false); setStore(""); setError(""); };
  return <section className="space-y-3 rounded-xl border border-white/15 p-4" aria-label="Emissão de cupom">
    {result ? <><p role="status">{result.message}</p>{result.state === "issued" && <>
      {(result.order?.redeem_code || result.order?.coupon_number) && <p className="select-all break-all rounded-lg bg-black p-3 font-mono text-xl cashback-accent-text">{result.order.redeem_code || result.order.coupon_number}</p>}
      <p className="whitespace-pre-line text-sm">{result.order?.redeem_instruction}</p>
      {result.order?.number && <p className="text-xs text-zinc-400">Comprovante: {result.order.number}</p>}
    </>}{result.state === "rejected" && <Button onClick={reset}>Revisar e tentar novamente</Button>}<p className="text-sm text-zinc-400">Seus comprovantes ficam em Meus cupons.</p></> : <>
      {!confirming && !scanning && <Button onClick={() => { setError(""); offer.template === "qrcode" ? setScanning(true) : setConfirming(true); }}>{offer.template === "qrcode" ? "Ler QR Code da loja" : "Gerar código do cupom"}</Button>}
      {scanning && <><p className="text-sm">Aponte a câmera para o QR Code fornecido pela loja.</p><div id={readerId} className="min-h-56 overflow-hidden rounded-xl" /><Button onClick={() => setScanning(false)}>Cancelar leitura</Button></>}
      {confirming && <><p className="text-sm">Confirma a emissão deste cupom conforme as regras da oferta? Esta ação poderá consumir o limite de usos do benefício.</p>{store && <p className="text-sm text-zinc-400">CNPJ da loja lido: {store}</p>}<div className="flex flex-wrap gap-2"><Button disabled={busy} onClick={submit}>{busy ? "Emitindo..." : "Confirmar emissão"}</Button><Button disabled={busy} variant="outline" onClick={() => { setConfirming(false); setStore(""); }}>Cancelar</Button></div></>}
    </>}
    {error && <p role="alert" className="text-sm text-amber-300">{error}</p>}
  </section>;
}
